import { useQuery } from '@tanstack/react-query';
import { Modal, Spin } from 'antd';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';

import './PreviewFileModal.scss';
import { fileTypeMap } from '../../../constants';
import FileManagerService from '../../../../../services/FileManager/fileManager.service';
import type { FileNode } from '../../../../../types/FileManagerTypes';

const { getViewLink } = FileManagerService();

// react-doc-viewer has no audio renderer and we render mp4 natively, so these
// two categories bypass DocViewer for a plain HTML media tag.
const stripDot = (exts: string[]): Set<string> => new Set(exts.map((e) => e.replace(/^\./, '')));
const AUDIO_EXTS = stripDot(fileTypeMap.Audio);
const VIDEO_EXTS = stripDot(fileTypeMap.Video);

const getExtension = (name?: string): string => name?.split('.').pop()?.toLowerCase() ?? '';

interface PreviewFileModalProps {
  show: boolean;
  hide: () => void;
  selectedFile?: FileNode;
}

export const PreviewFileModal = ({ show, hide, selectedFile }: PreviewFileModalProps) => {
  const { data, isLoading, isError } = useQuery(getViewLink(selectedFile?.id, show));

  const ext = getExtension(selectedFile?.name);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="preview-file-modal__status">
          <Spin />
        </div>
      );
    }
    if (isError || !data) return <div className="preview-file-modal__status" />;

    const { url } = data;

    if (AUDIO_EXTS.has(ext)) return <audio className="preview-file-modal__audio" src={url} controls />;
    if (VIDEO_EXTS.has(ext)) return <video className="preview-file-modal__video" src={url} controls />;

    // Everything else goes through react-doc-viewer. Office docs
    // (doc/docx/xls/xlsx/ppt/pptx) render via Microsoft's Office Online viewer
    // in an iframe: it needs a publicly reachable URL (the presigned S3 link
    // works in deployed envs but NOT against a localhost-only backend) and the
    // file transits Microsoft's service. Passing `fileType` explicitly skips
    // DocViewer's default HEAD prefetch, which 403s on a presigned GET URL.
    return (
      <DocViewer
        className="preview-file-modal__doc"
        documents={[{ uri: url, fileType: ext }]}
        pluginRenderers={DocViewerRenderers}
        config={{
          header: { disableHeader: true },
          pdfVerticalScrollByDefault: true,
        }}
      />
    );
  };

  return (
    <Modal
      open={show}
      onCancel={hide}
      centered
      maskClosable
      footer={null}
      width="90vw"
      title={data?.fileName ?? selectedFile?.name}
      className="preview-file-modal"
    >
      <div className="preview-file-modal__body">{renderContent()}</div>
    </Modal>
  );
};
